import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ColumnDef, PaginationState } from '@tanstack/react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { DataTable, DataTableHandle } from '@/components/ui/DataTable';
import {
  ColumnManagerPopover,
  ManagedColumn,
} from '@/components/ui/ColumnManagerPopover';
import { DateRangeFilter } from '@/components/ui/DateRangeFilter';
import { ExportCsvButton } from '@/components/ui/ExportCsvButton';
import { TableStateGate } from '@/components/ui/TableStateGate';
import SubmissionsTableFilter from '../SubmissionsTableFilter';
import PizzaTracker from '../PizzaTracker';
import { SubmissionFormData } from '@/features/submission_forms/types';
import {
  useSubmissions,
  type SubmissionListScope,
} from '@/hooks/submissions/useSubmissions';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { useDateRangeColumnFilters } from '@/hooks/useDateRangeColumnFilters';
import { useDebounce } from '@/hooks/useDebounce';
import { useUser } from '@/providers/UserProvider';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ViewDetailsButton } from '@/components/ui/ViewDetailsButton';
import { formatTableDate } from '@/utils/dates';

interface ScopedSubmissionsTableProps {
  ownerId?: string;
  studyId?: string;
  submissionScope?: SubmissionListScope;
  title?: string;
  className?: string;
  hideSearch?: boolean;
  /** Rows per page. Omit to render every row unpaginated (the default, and
   * what the Site and Submission show pages still do). Pagination is purely
   * client-side — the fetch below sends no page/perPage, so the date-range
   * filters and CSV export still see every row, not just the current page. */
  pageSize?: number;
}

const DEFAULT_COLUMN_ORDER = [
  'type',
  'title_or_site',
  'display_id',
  'status',
  'submitted_at',
  'complete_date',
  'view',
];

const MANAGEABLE_COLUMNS = [
  { id: 'type', defaultVisible: true, defaultPinned: 'left' as const },
  { id: 'title_or_site', defaultVisible: true },
  { id: 'display_id', defaultVisible: true },
  { id: 'status', defaultVisible: true },
  { id: 'submitted_at', defaultVisible: true },
  { id: 'complete_date', defaultVisible: true },
];

// Columns with a DateRangeFilter — kept in one place so the toolbar and the
// useDateRangeColumnFilters wiring below can't drift apart.
const DATE_RANGE_FILTER_COLUMNS = ['submitted_at', 'complete_date'];

export default function ScopedSubmissionsTable({
  ownerId,
  studyId,
  submissionScope,
  title,
  className = 'bg-white rounded-lg border border-gray-200',
  hideSearch = false,
  pageSize,
}: ScopedSubmissionsTableProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userData } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const dataTableRef = useRef<DataTableHandle>(null);
  const [exportRowCount, setExportRowCount] = useState(0);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize ?? 0,
  });
  const [selectedSubmission, setSelectedSubmission] = useState<{
    id: string;
    title: string;
    status: string;
    display_id: string;
    submission_type?: 'Study' | 'StudySite' | 'Submission';
    owner_id?: string;
  } | null>(null);

  // Every usage of this table needs its own persisted column layout — scope
  // alone isn't enough: Sites/Show.tsx (ownerId=site.id, scope='site') and
  // the "Site Level Submissions" section on Studies/Show.tsx (studyId=study.id,
  // scope='site') both have scope='site' but list different owners' rows, so
  // the owner/study id has to be folded in too or they'd share (and silently
  // overwrite) the same localStorage entry.
  const ownerKey = ownerId ?? studyId ?? 'all';
  const tableId = `submissions-${submissionScope ?? 'all'}-${ownerKey}`;
  const isSiteScope = submissionScope === 'site';

  const titleOrSiteLabel = isSiteScope ? 'Site Name' : 'Title';

  const columnLabels: Record<string, string> = {
    type: 'Type',
    title_or_site: titleOrSiteLabel,
    display_id: 'Submission ID',
    status: 'Status',
    submitted_at: 'Submitted',
    complete_date: 'Completed Date',
  };

  const managedColumns: ManagedColumn[] = MANAGEABLE_COLUMNS.map(({ id }) => ({
    id,
    label: columnLabels[id],
  }));

  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    resetToDefaults,
  } = useColumnPreferences({
    tableId,
    userId: userData?.id,
    columns: MANAGEABLE_COLUMNS,
    defaultOrder: DEFAULT_COLUMN_ORDER,
  });

  const { columnFilters, setColumnFilters, dateRangeValue, setDateRangeValue } =
    useDateRangeColumnFilters({
      dateRangeColumns: DATE_RANGE_FILTER_COLUMNS,
      searchParams,
    });

  const handleViewSubmission = useCallback((submission: SubmissionFormData) => {
    setSelectedSubmission(submission);
    setIsDrawerOpen(true);
  }, []);

  const onApplyFilter = (filters: Array<{ key: string; value: string }>) => {
    filters.forEach(filter => {
      if (filter.key === 'status') {
        setStatus(filter.value);
      } else if (filter.key === 'type') {
        setType(filter.value);
      }
    });
  };

  // Memoize the row click handler to prevent unnecessary re-renders
  const handleRowClick = useCallback(
    (
      submission: SubmissionFormData,
      event: React.MouseEvent | React.KeyboardEvent
    ) => {
      // Mouse only: let right-click and middle-click behave normally. A
      // keyboard activation has no `button`, so it must not be filtered here.
      if ('button' in event && event.button !== 0) return;

      const url = submission.show_path;

      // Allow Ctrl/Cmd+click to open in new tab
      if (event.ctrlKey || event.metaKey) {
        window.open(url, '_blank');
        return;
      }

      // Use backend-provided navigation logic
      navigate(url);
    },
    [navigate]
  );

  // Use React Query for data fetching with caching
  const {
    data = [],
    isLoading,
    error,
  } = useSubmissions({
    ownerId,
    studyId,
    submissionScope,
    search: debouncedSearchQuery,
    status,
    type,
    order: 'desc',
    sort: 'created_at',
  });

  // Any change that narrows the row set has to send the reader back to page
  // one — otherwise a filter applied while on page 3 leaves them staring at a
  // table that now has only one page of results.
  useEffect(() => {
    setPagination(prev =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
  }, [debouncedSearchQuery, status, type, columnFilters]);

  // Memoize column definitions to prevent table re-renders
  const columns = useMemo(
    () =>
      [
        {
          header: 'Type',
          id: 'type',
          accessorKey: 'type',
          enableSorting: true,
          // truncate: long type names (e.g. 'Initial Review of Protocol') ellipsis instead of
          // pushing neighboring columns off screen.
          meta: { overflow: 'truncate', maxWidth: 180 },
        },
        {
          header: titleOrSiteLabel,
          id: 'title_or_site',
          accessorFn: (row: SubmissionFormData) =>
            isSiteScope ? row.site_name : row.title,
          enableSorting: true,
          meta: { overflow: 'truncate', minWidth: 200 },
          // 'truncate' — DataTable wraps in a truncate div with title tooltip
        },
        {
          header: 'Submission ID',
          id: 'display_id',
          accessorKey: 'display_id',
          enableSorting: true,
          meta: { overflow: 'nowrap', maxWidth: 130 },
        },
        {
          header: 'Status',
          id: 'status',
          accessorKey: 'status',
          enableSorting: true,
          meta: { overflow: 'nowrap', maxWidth: 120 },
          cell: ({ getValue }: { getValue: () => string }) => (
            <StatusBadge status={getValue()} />
          ),
        },
        {
          header: 'Submitted',
          id: 'submitted_at',
          accessorKey: 'submitted_at',
          enableSorting: true,
          sortingFn: 'nullableDateSort' as const,
          filterFn: 'dateRange' as const,
          meta: {
            overflow: 'nowrap',
            maxWidth: 100,
            csvFormatter: value => formatTableDate(value as string | null),
          },
          cell: ({ getValue }: { getValue: () => string | null }) =>
            formatTableDate(getValue()),
        },
        {
          header: 'Completed Date',
          id: 'complete_date',
          accessorKey: 'complete_date',
          enableSorting: true,
          sortingFn: 'nullableDateSort' as const,
          filterFn: 'dateRange' as const,
          meta: {
            overflow: 'nowrap',
            maxWidth: 130,
            csvFormatter: value => formatTableDate(value as string | null),
          },
          cell: ({ getValue }: { getValue: () => string | null }) =>
            formatTableDate(getValue()),
        },
        {
          id: 'view',
          header: '',
          enableSorting: false,
          meta: { overflow: 'nowrap', exportable: false },
          cell: ({ row }: { row: { original: SubmissionFormData } }) => (
            <ViewDetailsButton
              onClick={() => handleViewSubmission(row.original)}
            />
          ),
        },
      ] as ColumnDef<SubmissionFormData, unknown>[],
    [handleViewSubmission, isSiteScope, titleOrSiteLabel]
  );

  return (
    <>
      <div className={className}>
        {title && (
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="text-gray-900 font-semibold">{title}</h3>
          </div>
        )}
        {/* Search and Filters — hidden only when hideSearch is set */}
        {!hideSearch && (
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by title or id"
                  className="pl-10 w-80"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              <SubmissionsTableFilter onApplyFilter={onApplyFilter} />
              <DateRangeFilter
                label="Submitted"
                value={dateRangeValue('submitted_at')}
                onChange={setDateRangeValue('submitted_at')}
              />
              <DateRangeFilter
                label="Completed Date"
                value={dateRangeValue('complete_date')}
                onChange={setDateRangeValue('complete_date')}
              />
              <div className="ml-auto flex items-center gap-2">
                <ExportCsvButton
                  dataTableRef={dataTableRef}
                  filename={`${tableId}.csv`}
                  rowCount={exportRowCount}
                />
                <ColumnManagerPopover
                  columns={managedColumns}
                  columnVisibility={columnVisibility}
                  onColumnVisibilityChange={setColumnVisibility}
                  columnOrder={columnOrder}
                  onColumnOrderChange={setColumnOrder}
                  columnPinning={columnPinning}
                  onColumnPinningChange={setColumnPinning}
                  onReset={resetToDefaults}
                />
              </div>
            </div>
          </div>
        )}

        {/* Submissions Table */}
        <TableStateGate
          isLoading={isLoading}
          error={error}
          isEmpty={data.length === 0}
          errorMessage={
            (error as Error)?.message || 'Failed to load submissions'
          }
          emptyMessage="No submissions yet."
        >
          <DataTable
            ref={dataTableRef}
            columns={columns}
            data={data}
            onRowClick={handleRowClick}
            emptyMessage="No submissions yet."
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            columnOrder={columnOrder}
            onColumnOrderChange={setColumnOrder}
            columnPinning={columnPinning}
            onColumnPinningChange={setColumnPinning}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            {...(pageSize
              ? {
                  pagination: {
                    pageIndex: pagination.pageIndex,
                    pageSize: pagination.pageSize,
                    onPaginationChange: setPagination,
                  },
                }
              : {})}
            onExportRowCountChange={setExportRowCount}
          />
        </TableStateGate>
      </div>
      {selectedSubmission && (
        <PizzaTracker
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          selectedSubmission={selectedSubmission}
        />
      )}
    </>
  );
}
